/**
 * wakeLock.service.js
 * Mantém a tela ligada durante a visualização de repertório e palco.
 * Suporta Native (Capacitor iOS & Android via TriadLink / WindowManager)
 * e Web/PWA padrão (W3C Screen Wake Lock API).
 */

import { Capacitor } from '@capacitor/core';
import { TriadLink } from '@/native/triad-link';

class WakeLockService {
    constructor() {
        this.sentinel = null;
        this.isActive = false;
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.init();
    }

    init() {
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    async handleVisibilityChange() {
        if (document.visibilityState === 'visible' && this.isActive) {
            console.log('[WakeLock] App voltou para o primeiro plano, re-adquirindo trava de tela...');
            await this.acquireInternal();
        }
    }

    isNative() {
        try {
            return Capacitor.isNativePlatform();
        } catch (e) {
            return false;
        }
    }

    async acquire() {
        this.isActive = true;
        await this.acquireInternal();
    }

    async acquireInternal() {
        if (!this.isActive) return;

        // 1. Em app nativo (Android/iOS): ativa trava via TriadLink (FLAG_KEEP_SCREEN_ON / isIdleTimerDisabled)
        if (this.isNative()) {
            try {
                if (typeof TriadLink.setKeepAwake === 'function') {
                    await TriadLink.setKeepAwake({ enable: true });
                    console.log('[WakeLock] Trava de tela nativa ativada (TriadLink)');
                }
            } catch (err) {
                console.warn('[WakeLock] Falha ao acionar trava nativa:', err);
            }
        }

        // 2. No Web / PWA ou como camada adicional no WebView: W3C Screen Wake Lock API
        if (typeof navigator !== 'undefined' && 'wakeLock' in navigator) {
            try {
                if (this.sentinel && !this.sentinel.released) {
                    return;
                }
                this.sentinel = await navigator.wakeLock.request('screen');
                this.sentinel.addEventListener('release', () => {
                    console.log('[WakeLock] W3C Screen wake lock liberado');
                });
                console.log('[WakeLock] W3C Screen wake lock adquirido');
            } catch (err) {
                // NotAllowedError é comum se não houver interação prévia ou bateria ultra-baixa
                console.warn('[WakeLock] Não foi possível adquirir W3C Screen wake lock:', err);
            }
        }
    }

    async release() {
        this.isActive = false;

        // Libera trava nativa
        if (this.isNative()) {
            try {
                if (typeof TriadLink.setKeepAwake === 'function') {
                    await TriadLink.setKeepAwake({ enable: false });
                    console.log('[WakeLock] Trava de tela nativa desativada');
                }
            } catch (err) {
                console.warn('[WakeLock] Falha ao desativar trava nativa:', err);
            }
        }

        // Libera W3C Screen Wake Lock
        if (this.sentinel) {
            try {
                await this.sentinel.release();
            } catch (err) {
                console.warn('[WakeLock] Erro ao liberar sentinel:', err);
            }
            this.sentinel = null;
        }
    }

    destroy() {
        this.release();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }
}

export const wakeLockService = new WakeLockService();
export default wakeLockService;
